import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Globe, ArrowLeft, Sparkle, Code, Palette, Rocket } from '@phosphor-icons/react';

const WebsiteGeneratorPage = () => {
  const { user } = useAuth();
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  
  // Only admin can access
  if (user?.role !== 'admin') {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#0a0a0a' }}>
        <div className="text-center p-8 max-w-md">
          <Globe size={64} className="mx-auto mb-6" style={{ color: '#8B5CF6' }} />
          <h1 className="text-2xl font-bold mb-4" style={{ color: '#fff' }}>Admin Access Required</h1>
          <p className="mb-6" style={{ color: '#888' }}>This tool is only available to site administrators.</p>
          <Link 
            to="/community" 
            className="inline-block px-6 py-3 rounded-lg font-semibold"
            style={{ backgroundColor: '#8B5CF6', color: '#fff' }}
          >
            Back to Community
          </Link>
        </div>
      </div>
    );
  }

  const templates = [
    {
      id: 'business',
      name: 'Business Landing Page',
      description: 'Professional landing page for your business or service',
      icon: Rocket,
      color: '#10B981'
    },
    {
      id: 'portfolio',
      name: 'Personal Portfolio',
      description: 'Showcase your work and professional experience',
      icon: Palette,
      color: '#F59E0B'
    },
    {
      id: 'saas',
      name: 'SaaS Product Page',
      description: 'Modern product page with pricing and features',
      icon: Code,
      color: '#3B82F6'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#0a0a0a' }}>
      {/* Header */}
      <header 
        className="sticky top-0 z-50 px-6 py-4 flex items-center justify-between"
        style={{ backgroundColor: '#111', borderBottom: '1px solid #222' }}
      >
        <Link 
          to="/community" 
          className="flex items-center gap-2 text-sm hover:opacity-80 transition-opacity"
          style={{ color: '#8B5CF6' }}
        >
          <ArrowLeft size={18} />
          Back to Community
        </Link>
        
        <div className="flex items-center gap-3">
          <Globe size={24} style={{ color: '#8B5CF6' }} />
          <span className="text-lg font-bold" style={{ color: '#fff', fontFamily: "'Playfair Display', serif" }}>
            Website Generator
          </span>
          <span 
            className="px-2 py-1 text-xs font-bold rounded"
            style={{ backgroundColor: 'rgba(139, 92, 246, 0.2)', color: '#8B5CF6' }}
          >
            ADMIN TOOL
          </span>
        </div>

        <div className="w-32" />
      </header>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-6 py-12">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-6" style={{ backgroundColor: 'rgba(139, 92, 246, 0.1)' }}>
            <Sparkle size={16} style={{ color: '#8B5CF6' }} />
            <span className="text-sm font-medium" style={{ color: '#8B5CF6' }}>AI-Powered Website Creation</span>
          </div>
          <h1 className="text-4xl font-bold mb-4" style={{ color: '#fff', fontFamily: "'Playfair Display', serif" }}>
            Build Beautiful Websites in Minutes
          </h1>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#888' }}>
            Select a template and let AI generate a stunning, fully-functional website 
            tailored to your brand and business needs.
          </p>
        </div>

        {/* Template Selection */}
        <div className="grid md:grid-cols-3 gap-6 mb-12">
          {templates.map((template) => (
            <button
              key={template.id}
              onClick={() => setSelectedTemplate(template.id)}
              className={`p-6 rounded-xl text-left transition-all ${
                selectedTemplate === template.id 
                  ? 'ring-2 ring-offset-2 ring-offset-[#0a0a0a]' 
                  : 'hover:scale-105'
              }`}
              style={{ 
                backgroundColor: '#111', 
                border: `1px solid ${selectedTemplate === template.id ? template.color : '#222'}`,
                ringColor: template.color
              }}
            >
              <div 
                className="w-12 h-12 rounded-lg flex items-center justify-center mb-4"
                style={{ backgroundColor: `${template.color}20` }}
              >
                <template.icon size={24} style={{ color: template.color }} />
              </div>
              <h3 className="text-lg font-bold mb-2" style={{ color: '#fff' }}>{template.name}</h3>
              <p className="text-sm" style={{ color: '#888' }}>{template.description}</p>
            </button>
          ))}
        </div>

        {/* Coming Soon Notice */}
        <div className="text-center p-8 rounded-xl" style={{ backgroundColor: '#111', border: '1px solid #222' }}>
          <Sparkle size={48} className="mx-auto mb-4" style={{ color: '#8B5CF6' }} />
          <h2 className="text-2xl font-bold mb-3" style={{ color: '#fff' }}>Coming Soon</h2>
          <p className="max-w-xl mx-auto mb-6" style={{ color: '#888' }}>
            The Website Generator is currently under development. Soon you'll be able to 
            create stunning, AI-generated websites with just a few clicks.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#222' }}>
              <span className="text-sm" style={{ color: '#888' }}>AI Content Generation</span>
            </div>
            <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#222' }}>
              <span className="text-sm" style={{ color: '#888' }}>Custom Branding</span>
            </div>
            <div className="px-4 py-2 rounded-lg" style={{ backgroundColor: '#222' }}>
              <span className="text-sm" style={{ color: '#888' }}>One-Click Deploy</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WebsiteGeneratorPage;
